/**
 * Qr — renders a real, scannable QR code as a crisp SVG. Wraps the zero-dep
 * `qrcode-generator` encoder; modules are emitted as one black `<path>` on a
 * transparent ground (the container supplies the white quiet-zone). Presentational
 * only — `value` is whatever deep link the caller wants the phone to open.
 */

import { useMemo } from 'react';
import qrcode from 'qrcode-generator';

export function Qr({ value, className }: { value: string; className?: string }): JSX.Element | null {
    const path = useMemo(() => {
        if (!value) {
            return null;
        }

        const qr = qrcode(0, 'M');

        qr.addData(value);
        qr.make();

        const count = qr.getModuleCount();
        const parts: string[] = [];

        for (let row = 0; row < count; row++) {
            for (let col = 0; col < count; col++) {
                if (qr.isDark(row, col)) {
                    parts.push(`M${col} ${row}h1v1h-1z`);
                }
            }
        }

        return { d: parts.join(''), count };
    }, [value]);

    if (!path) {
        return null;
    }

    return (
        <svg
            className = { className }
            viewBox = { `0 0 ${path.count} ${path.count}` }
            width = "100%"
            height = "100%"
            shapeRendering = "crispEdges"
            role = "img"
            aria-label = "QR code">
            <path d = { path.d } fill = "#0a0a0b" />
        </svg>
    );
}
